import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown } from 'react-feather';
import { IdentificationIcon } from '@heroicons/react/24/outline';
import UserGroupIcon from '../../../../../public/assets/Icons-Users.svg';
import NoAccessScreen from '../NoAccessScreen';
import {
  getStatusLabel,
  getStatusBadgeClasses,
} from '@/shared/utils/statusBadge';
import {
  listFacilityUsers,
  type FacilityUserListItem,
} from '../../api/listFacilityUserApiService';
import { FACILITY_LABELS } from '../../constants/facilityLabels';

const GENDER_LABEL: Record<string, string> = {
  male: FACILITY_LABELS.FIELD_EMPLOYEE_GENDER_MALE,
  female: FACILITY_LABELS.FIELD_EMPLOYEE_GENDER_FEMALE,
};

const LIMIT = 20;

// ─── UserAccordionCard ────────────────────────────────────────────────────────

interface UserAccordionCardProps {
  user: FacilityUserListItem;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}

const UserAccordionCard = ({
  user,
  index,
  expanded,
  onToggle,
}: UserAccordionCardProps) => {
  const badgeClasses = getStatusBadgeClasses(user.status);
  const statusLabel = getStatusLabel(user.status);

  const phone = user.phone_number
    ? `${user.countryCode ?? '+48'} ${user.phone_number}`
    : '—';

  return (
    <div
      data-testid={`employee-list-item-${index}`}
      className="border border-[#DCDDE0] rounded-xl overflow-hidden"
    >
      <div
        className="p-4 flex items-center gap-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="w-10 h-10 rounded-full bg-neutral-90 flex items-center justify-center shrink-0">
          <UserGroupIcon className="w-6 h-6 text-on-surface-variant" />
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <p className="text-bodyMedium text-neutral-10 break-words">
            {user.name}
          </p>
          <div className="flex items-center gap-1">
            <IdentificationIcon className="w-4 h-4 text-outline shrink-0" />
            <span className="text-bodySmall text-outline">
              {user.user_id ?? '—'}
            </span>
          </div>
        </div>

        <span
          className={`shrink-0 px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${badgeClasses.container}`}
          style={{ fontSize: '14px', lineHeight: '20px' }}
        >
          <div
            className={`rounded-full shrink-0 ${badgeClasses.dot}`}
            style={{ width: '6px', height: '6px' }}
          />
          {statusLabel}
        </span>

        <button
          type="button"
          data-testid={`employee-accordion-toggle-${index}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#474A4C1A] active:bg-[#474A4C14] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] transition-transform duration-200"
          aria-label={`${expanded ? 'Zwiń' : 'Rozwiń'} szczegóły pracownika ${user.name}`}
          aria-expanded={expanded}
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <ChevronDown size={24} strokeWidth={1.5} color="#474A4C" />
        </button>
      </div>

      {expanded && (
        <div className="px-4 pt-2 pb-4 flex flex-col gap-4">
          <p
            data-testid="employee-section-header"
            className="text-[#474A4C] font-normal"
            style={{ fontSize: '18px', lineHeight: '26px' }}
          >
            {FACILITY_LABELS.SECTION_EMPLOYEE_FORM}
          </p>
          <div className="grid grid-cols-2 gap-x-16 gap-y-3">
            <div className="flex flex-col gap-[4px]">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                {FACILITY_LABELS.FIELD_EMPLOYEE_EMAIL}
              </p>
              <p
                data-testid="employee-email-value"
                className="text-[#181A1B] break-words"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {user.email || '—'}
              </p>
            </div>
            <div className="flex flex-col gap-[4px]">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                {FACILITY_LABELS.FIELD_PHONE}
              </p>
              <p
                data-testid="employee-phone-value"
                className="text-[#181A1B]"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {phone}
              </p>
            </div>
            <div className="flex flex-col gap-[4px]">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                {FACILITY_LABELS.FIELD_EMPLOYEE_GENDER}
              </p>
              <p
                data-testid="employee-gender-value"
                className="text-[#181A1B]"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {user.gender ? (GENDER_LABEL[user.gender] ?? '—') : '—'}
              </p>
            </div>
            <div className="flex flex-col gap-[4px]">
              <p
                className="text-[#474A4C]"
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                }}
              >
                {FACILITY_LABELS.FIELD_EMPLOYEE_ACADEMIC_TITLE}
              </p>
              <p
                data-testid="employee-academic-title-value"
                className="text-[#181A1B] break-words"
                style={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                }}
              >
                {user.academic_title || '—'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── EmployeeAccordionList ────────────────────────────────────────────────────

interface EmployeeAccordionListProps {
  facilityId: string;
  isValid?: boolean;
  onTotalCountChange?: (count: number) => void;
  onAccessDenied?: () => void;
}

export const EmployeeAccordionList = ({
  facilityId,
  isValid,
  onTotalCountChange,
  onAccessDenied,
}: EmployeeAccordionListProps) => {
  const [items, setItems] = useState<FacilityUserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isLoading) {
      setPage((prev) => prev + 1);
    }
  }, [hasNextPage, isLoading]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasNextPage(false);
    setExpandedId(null);
    setAccessDenied(false);
  }, [facilityId]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    listFacilityUsers({
      facilityId,
      page,
      limit: LIMIT,
      ...(isValid !== undefined && { isValid }),
    })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.accessDenied) {
          onAccessDenied?.();
          setAccessDenied(true);
          return;
        }
        if (result.success && result.data) {
          const incoming = result.data.data;
          setItems((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
          setHasNextPage(result.data.pagination.hasNextPage);
          if (page === 1) {
            onTotalCountChange?.(result.data.pagination.totalCount);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, page, isValid, onTotalCountChange, onAccessDenied]);

  if (accessDenied) {
    return (
      <NoAccessScreen message="You don't have access to view employees." />
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 1 }).map((_, idx) => (
          <div
            key={idx}
            className="border border-[#DCDDE0] rounded-xl px-4 py-3 flex items-center gap-3 animate-pulse"
          >
            <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
            <div className="w-20 h-6 bg-gray-200 rounded-[8px] shrink-0" />
            <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <div className="rounded-xl bg-white p-4">
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            color: '#474A4C',
            fontSize: 14,
            fontFamily: 'Roboto',
            fontWeight: '400',
            lineHeight: '20px',
            letterSpacing: 0.25,
            wordWrap: 'break-word',
          }}
        >
          No employees added yet.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((user, idx) => (
        <UserAccordionCard
          key={user.id}
          user={user}
          index={idx}
          expanded={expandedId === user.id}
          onToggle={() =>
            setExpandedId((prev) => (prev === user.id ? null : user.id))
          }
        />
      ))}

      {isLoading &&
        Array.from({ length: 2 }).map((_, idx) => (
          <div
            key={`shimmer-${idx}`}
            className="border border-[#DCDDE0] rounded-xl px-4 py-3 flex items-center gap-3 animate-pulse"
          >
            <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
            <div className="w-20 h-6 bg-gray-200 rounded-[8px] shrink-0" />
            <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
          </div>
        ))}

      <div ref={sentinelRef} />
    </div>
  );
};
